import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || "all";
  const q = params.get("q") || "";
  const featuredOnly = params.get("featured") === "1";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("popular");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (activeCategory !== "all") p.category = activeCategory;
    if (q) p.q = q;
    if (featuredOnly) p.featured = true;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [activeCategory, q, featuredOnly]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => (a.plans?.[0]?.price || 0) - (b.plans?.[0]?.price || 0));
    else if (sort === "price-desc") arr.sort((a, b) => (b.plans?.[0]?.price || 0) - (a.plans?.[0]?.price || 0));
    else if (sort === "rating") arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return arr;
  }, [products, sort]);

  const setCat = (slug) => {
    const p = new URLSearchParams(params);
    if (slug === "all") p.delete("category"); else p.set("category", slug);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-14">
      <div className="mb-10">
        <div className="kyro-overline">Shop</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-3">
          {q ? <>Results for “{q}”</> : featuredOnly ? "Featured drops" : "Every premium subscription"}
        </h1>
        <p className="text-kyro-mute mt-3 max-w-2xl">Verified, monitored, and delivered instantly. Filter by category to focus your hunt.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 overflow-x-auto pb-2 -mx-1 mb-8">
        <button data-testid="filter-all" onClick={() => setCat("all")} className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${activeCategory === "all" ? "bg-[#FF4433] text-white border-[#FF4433]" : "border-white/10 hover:bg-white/5"}`}>All</button>
        {categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`filter-${c.slug}`}
            onClick={() => setCat(c.slug)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${activeCategory === c.slug ? "bg-[#FF4433] text-white border-[#FF4433]" : "border-white/10 hover:bg-white/5"}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-kyro-mute">{sorted.length} product{sorted.length === 1 ? "" : "s"}</p>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="sort-select"
          className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none"
        >
          <option value="popular">Sort: Popular</option>
          <option value="rating">Highest rated</option>
          <option value="price-asc">Price: Low to high</option>
          <option value="price-desc">Price: High to low</option>
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="kyro-card overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-white/5" />
              <div className="p-5 space-y-3">
                <div className="h-3 bg-white/5 rounded w-1/3" />
                <div className="h-5 bg-white/5 rounded w-2/3" />
                <div className="h-4 bg-white/5 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="kyro-card p-12 text-center">
          <Search className="w-8 h-8 mx-auto text-kyro-mute mb-4" />
          <h3 className="font-display font-bold text-xl">Nothing found</h3>
          <p className="text-kyro-mute mt-2">Try a different keyword or clear filters.</p>
          <Link to="/shop" className="kyro-btn-secondary inline-block mt-6">Clear filters</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {sorted.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      )}
    </div>
  );
}
